import { z } from 'zod';

/**
 * What the platform's moderation has decided about one message (N20, P20).
 *
 * P18a **stores and reads** this column; P20 is what will write anything other
 * than the default. Carrying it from the start is deliberate — a column added
 * later means a migration over a table that by then holds every message on the
 * platform — but a column that is written and never read would be worse: it
 * would look like a control while the message it judged was delivered anyway,
 * and closing that hole would be a thing P20 had to *remember*. So the read path
 * honours it now (U17).
 *
 * - `allowed` — nothing objected. **The default**, because on this phase nothing
 *   classifies anything: with no moderation in the system, refusing to deliver
 *   an unjudged message would refuse every message.
 * - `pending` — classification is in flight. P20's asynchronous path lands here;
 *   it is treated as deliverable, because a member whose message hangs until an
 *   AI answers has a broken chat, and N15's guards can fail closed on the
 *   *classification* without failing the conversation.
 * - `hidden` — an enforcement action hid it (N20). It is withheld from the
 *   **recipient** and still shown to its **sender**, who is told separately by
 *   the enforcement notice; a message that silently vanished from its author's
 *   own screen would read as a bug in the app rather than as a consequence.
 */
export const MESSAGE_MODERATION_STATUSES = ['allowed', 'pending', 'hidden'] as const;

export const MessageModerationStatusSchema = z.enum(MESSAGE_MODERATION_STATUSES);

export type MessageModerationStatus = (typeof MESSAGE_MODERATION_STATUSES)[number];

/** What a message is stored as until something judges it. */
export const INITIAL_MESSAGE_MODERATION_STATUS: MessageModerationStatus = 'allowed';

/**
 * Does this status withhold the message from anyone other than its sender?
 *
 * The single spelling of the rule. The history query's SQL predicate and any
 * future in-memory filter must be built from this and only this — two spellings
 * of "what is hidden" is how a moderated message reaches the person it was
 * hidden from.
 */
export function isWithheldFromRecipient(status: MessageModerationStatus): boolean {
  return status === 'hidden';
}
